/**
 * Error Boundary Component
 * Catches React errors and displays fallback UI
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { canReloadForChunkError, errorScreenFor, isReloadingForChunkError, isRouteChunkError, reloadOnceForChunkError } from '../lib/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  reloadRefused: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorId: '',
    reloadRefused: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: `LPE-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      reloadRefused: false,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('LinguistPro Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);

    // Only route-level page chunks auto-reload; other chunk errors keep the fault card.
    if (isRouteChunkError(error) && !reloadOnceForChunkError() && !isReloadingForChunkError()) {
      this.setState({ reloadRefused: true });
    }
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorId: '', reloadRefused: false });
  };

  private handleHardReload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // A route chunk error is about to reload (componentDidCatch runs right
      // after this render), so show "Updating" now rather than flash the fault card.
      const updating =
        isReloadingForChunkError() ||
        (!this.state.reloadRefused && isRouteChunkError(this.state.error) && canReloadForChunkError());
      const screen = errorScreenFor(this.state.error, updating);

      if (screen === 'updating') {
        return (
          <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
            <div className="max-w-lg w-full bg-slate-900 rounded-3xl p-12 border border-slate-800 shadow-2xl text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#00d4b8]/10 flex items-center justify-center border border-[#00d4b8]/20 mx-auto mb-8">
                <div className="w-7 h-7 border-2 border-slate-700 border-t-[#00d4b8] rounded-full animate-spin" />
              </div>
              <h2 className="text-xl font-black text-white uppercase tracking-wider mb-2">
                Updating LinguistPro
              </h2>
              <p className="text-slate-400 text-sm font-medium">
                A newer version is loading. This takes a second.
              </p>
            </div>
          </div>
        );
      }

      const isChunkError = screen === 'chunk-fault';

      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
          <div className="max-w-lg w-full bg-slate-900 rounded-3xl p-12 border border-rose-500/20 shadow-2xl">
            {/* Header */}
            <div className="flex items-center gap-5 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/10 flex items-center justify-center border border-rose-500/20">
                <svg
                  className="w-7 h-7 text-rose-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-black text-white uppercase tracking-wider">
                  System Fault
                </h2>
                <p className="text-rose-400 text-sm font-medium">
                  Critical error detected
                </p>
              </div>
            </div>

            {/* Error Details */}
            <div className="bg-slate-950 rounded-2xl p-6 mb-6 border border-slate-800">
              <code className="text-rose-300 text-sm font-mono break-all block">
                {this.state.error?.message || 'Unknown error occurred'}
              </code>
            </div>

            {isChunkError && (
              <p className="text-sm text-slate-300 mb-6 px-2">
                LinguistPro was updated while this page was open. Press Ctrl+Shift+R (Cmd+Shift+R on a Mac) to load the latest version.
              </p>
            )}

            {/* Diagnostic ID */}
            <div className="flex items-center justify-between mb-8 px-2">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Diagnostic ID
              </span>
              <span className="text-[11px] font-mono text-slate-400">
                {this.state.errorId}
              </span>
            </div>

            {/* Reset Button */}
            <button
              onClick={isChunkError ? this.handleHardReload : this.handleReset}
              className="w-full py-4 bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all active:scale-[0.98] shadow-lg hover:shadow-rose-500/20"
            >
              Reset System
            </button>

            {/* Help Text */}
            <p className="text-[10px] text-slate-600 text-center mt-6 uppercase tracking-wider">
              If this error persists, please refresh the page
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
